import { autoPlacement, computePosition, offset } from '@floating-ui/dom';

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-image-interactive"]';

/**
 * Friendation component
 */
export const blockImageInteractive = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    const handlePopups = () => {
      const triggerArea: HTMLElement = component.querySelector('.pois');

      const triggers: HTMLButtonElement[] = Array.from(
        component.querySelectorAll('.poi')
      );

      const resetAll = () => {
        triggers.forEach((trigger) => {
          hideToolTip(trigger);
        });
        document.removeEventListener('keydown', onKeydown);
      };

      /**
       * Listen for the escape key. If the escape key is pressed, close all tooltips.
       * @param e KeyboardEvent
       */
      const onKeydown = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
          resetAll();
        }
      };

      /**
       * Listen for clicks outside of the trigger area. If the click is outside of the trigger area, close all tooltips.
       * @param e MouseEvent
       */
      const onDocumentClick = (e: MouseEvent) => {
        if (
          e.target instanceof Node &&
          !triggerArea.contains(e.target as Node)
        ) {
          resetAll();
        }
      };

      const showTooltip = (trigger: HTMLButtonElement) => {
        const tooltip = findTooltip(trigger);
        resetAll();
        tooltip.inert = false;
        trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', onKeydown);
        document.addEventListener('click', onDocumentClick);

        computePosition(trigger, tooltip, {
          middleware: [offset(16), autoPlacement()]
        }).then(({ x, y }) => {
          Object.assign(tooltip.style, {
            left: window.innerWidth > 767 ? `${x}px` : '0',
            top: `${y}px`
          });
        });
      };

      const hideToolTip = (trigger: HTMLButtonElement, focus?: boolean) => {
        const tooltip = findTooltip(trigger);
        tooltip.inert = true;
        trigger.setAttribute('aria-expanded', 'false');

        if (focus) {
          setTimeout(() => {
            trigger.focus();
          }, 100);
        }
      };

      const findTooltip = (trigger: HTMLButtonElement) => {
        const tooltip: HTMLElement = component.querySelector(
          `[id="${trigger.getAttribute('aria-controls')}"]`
        );

        return tooltip;
      };

      triggers.forEach((trigger) => {
        const tooltip = findTooltip(trigger);

        /** We disable the click function when we open a tooltip with the mouseenter event. This avoids users clicking on the button directly after triggering the mouseenter event, causing the tooltip to open and close instantly. See {@link clickTimeout} */
        let clickCanClose = true;

        /** Time in miliseconds */
        const clickTimeout = 1000;

        const onClick = (e: MouseEvent) => {
          e.preventDefault();

          if (tooltip.inert) {
            showTooltip(trigger);
          } else {
            if (clickCanClose) {
              hideToolTip(trigger, true);
            }
          }
        };

        const onMouseEnter = () => {
          if (tooltip.inert) {
            clickCanClose = false;
            showTooltip(trigger);

            setTimeout(() => {
              clickCanClose = true;
            }, clickTimeout);
          }
        };

        trigger.addEventListener('click', onClick);
        trigger.addEventListener('mouseenter', onMouseEnter);
      });

      /**
       * Clicking an the {@link triggerArea} will close all pois – except when the click is on a poi or a child of the poi
       * @param e MouseEvent
       */
      const onTriggerArea = (e: MouseEvent) => {
        if (
          <Node>e.target !== triggerArea &&
          triggerArea.contains(<Node>e.target)
        )
          return;

        resetAll();
      };

      triggerArea.addEventListener('click', onTriggerArea);
    };

    /**
     * The user can click a „Read more” link in each popup. This should then take them to the associated accordion panel. The panel should automatically open.
     */
    const handleAcc = () => {
      const triggers: HTMLAnchorElement[] = Array.from(
        component.querySelectorAll('.accTrigger')
      );

      triggers.forEach((trigger) => {
        const onClick = (e: MouseEvent) => {
          e.preventDefault();
          const id = trigger.href.split('#')[1];

          const targetAccordionPanel: HTMLElement = component.querySelector(
            `#${id}`
          );

          if (
            targetAccordionPanel &&
            targetAccordionPanel.getAttribute('aria-expanded') === 'false'
          ) {
            targetAccordionPanel.click();
          }
        };

        trigger.addEventListener('click', onClick);
      });
    };

    handlePopups();
    handleAcc();
  });
};
